/*
Each pipe is modeled as a cell with a Left/Right and Top/Bottom port. Each pipe has 2 ports. 2 Pipes connect if their entrance and exit ports match. 
  --T--
  |   |
  L   R
  |   |
  --B--

TR (L) connects to LR (-) but not TB (|)
*/

export type PipeTag = '-' | '|' | '/' | '\\' | 'L' | 'J';
export type PipeLTBR = 'LR' | 'TB' | 'BR' | 'LB' | 'TR' | 'LT';
export type PipePort = 'L' | 'R' | 'T' | 'B';

export class Pipe {
  tag: PipeTag;
  ltbr: PipeLTBR

  flowing = false;

  constructor(tag: PipeTag) {
    this.tag = tag;
    this.ltbr = {
      '-': 'LR',
      '|': 'TB',
      '/': 'BR',
      '\\': 'LB',
      'L': 'TR',
      'J': 'LT'
    }[this.tag] as PipeLTBR;
  }

  static from(pipe : Partial<Pipe>) {
    return new Pipe(pipe?.tag ?? '-');
  }

  static horz = new Pipe('-');
  static vert = new Pipe('|');
  static lb = new Pipe('\\');
  static tr = new Pipe('L');
  static lt = new Pipe('J');
  static br = new Pipe('/');
  static options = [ this.horz, this.vert, this.tr, this.br, this.lt, this.lb ];

  nextPort(port: PipePort): PipePort | false {
    const ports = this.ltbr.split('') as PipePort[];
    const cp = this.connectingPort(port);
    return ports.includes(cp) ? ports.find(x => x != cp) as PipePort : false;
  }

  private connectingPort(port: PipePort): PipePort {
    return new Map<PipePort, PipePort>([
      [ 'L', 'R' ],
      [ 'R', 'L' ],
      [ 'T', 'B' ],
      [ 'B', 'T' ]
    ]).get(port) as PipePort;
  }
}

export class PipePuzzle {
  outflow: number;
  storage: number;
  station: number;

  pipes: Pipe[][] = [];
  endPipes = {
    outflow: Pipe.from(Pipe.horz),
    storage: Pipe.from(Pipe.horz),
    station: Pipe.from(Pipe.horz)
  };

  locks: number[] = []; // indexes of locked columns
  selected: number; // index of selected column, should NOT be locked

  constructor(outflow: number, storage: number, station: number, pipes: Pipe[][]) {
    this.outflow = outflow;
    this.storage = storage;
    this.station = station;
    this.pipes = pipes;

    this.locks = [];
    this.selected = 0;
  }

  static from(p : Partial<PipePuzzle>) {
    let puzzle = new PipePuzzle(p.outflow ?? 0, p.storage ?? 1, p.station ?? 2, p.pipes ?? []);
    puzzle.lock(p.locks ?? [2,3]);
    return puzzle;
  }

  private rotateColumn(c: number, n: number) {
    let col: Pipe[] = [];
    const numColums = this.numColumns();
    for (let r of this.pipes) {
      col.push(r[c] as Pipe);
    }
    let rotated: Pipe[] = [];
    for (let i = 0; i < col.length; i++) {
      rotated[i] = col[(i + n)%numColums] as Pipe;
    }
    for (let r = 0; r < this.pipes.length; r++) {
      this.pipes[r]![c] = rotated[r] as Pipe;
    }
  }

  unlockedColumns() {
    const numColums = this.numColumns();
    return [...Array(numColums).keys()].filter(x => !this.locks.includes(x));
  }

  display() {
    const rows = this.pipes.map(x => x.reduce((str, y) => str + y.tag, ''));
    return rows.join('\n') + '\n' + `Outflow: ${this.outflow} Station: ${this.station}`;
  }

  /** Also refresh flowing status of pipes */
  processSolution(target: 'station' | 'storage' = 'station') {
    // flush
    this.pipes.forEach(row => row.forEach(pipe => pipe.flowing = false));
    this.endPipes.storage.flowing = this.endPipes.station.flowing = false;

    this.endPipes.outflow.flowing = true;
    let [r, c] = [this.outflow - 1, 0];
    let lastPort: PipePort = 'R';
    while (true) {
      const pipe = (this.pipes[r] || [])[c];
      if (!pipe) break;
      let exitingPort = pipe.nextPort(lastPort) as PipePort;
      if (!exitingPort) break;
      pipe.flowing = true;
      switch(exitingPort) {
        case 'B': r++; break;
        case 'T': r--; break;
        case 'L': c--; break;
        case 'R': c++; break;
      }
      lastPort = exitingPort;
    }
    if (c != this.numColumns()) return false;
    if (r == this.storage - 1) {
      this.endPipes.storage.flowing = true;
      return target == 'storage';
    } else if (r == this.station - 1) {
      this.endPipes.station.flowing = true;
      return target == 'station';
    }
    return false;
  }

  numColumns() {
    return this.pipes[0]?.length ?? 0;
  }

  numRows() {
    return this.pipes.length ?? 0;
  }

  lock(cols: number[]) {
    this.locks = cols;
    this.selected = [...Array(this.numColumns()).keys()].find(c => !this.locks.includes(c)) ?? 0;
  }

  shiftSelectedColumn(direction: 'left' | 'right') {
    const nC = this.numColumns();
    for (let dC = 0; dC < nC; direction == 'right' ? dC++ : dC--) {
      let c = (this.selected + dC)%nC;
      if (c < 0) c = nC + c;
      if (c != this.selected && !this.locks.includes(c)) {
        this.selected = c;
        return this;
      }
    }
    return this;
  }

  rotateSelectedColumn(direction: 'up' | 'down') {
    this.rotateColumn(this.selected, direction == 'up' ? 1 : this.numRows() - 1);
    return this;
  }

  /** Mutates puzzle into solution/last attempt as side-effect */
  solveFor(target: 'station' | 'storage' = 'station') {
    // brute force
    const height = this.numRows();
    const [ c1, c2, c3 ] = [...Array(this.numColumns()).keys()].filter(c => !this.locks.includes(c));
    for (let i = 0; i < height; i++) { // leftmost column
      for (let j = 0; j < height; j++) {
        for (let k = 0; k < height; k++) {
          this.rotateColumn(c3 as number, 1);
          if (this.processSolution(target)) {
            return true;
          }
        }
        this.rotateColumn(c2 as number, 1);
      }
      this.rotateColumn(c1 as number, 1);
    }
    return false;
  }    
}